/**
 * Single source of truth for the facts the legal pages disclose.
 *
 * Modelled on the same file in the Paperstand website, which is operated by the
 * same sole trader — the operator block, the pending-placeholder convention and
 * the keys-not-prose rule are all carried over deliberately. What is *not*
 * carried over is the processor disclosure: Paperstand names three categories
 * and asserts a blanket Art. 28 DPA claim, which is thin for a platform that
 * routes other people's traffic through six named third parties. rii.link
 * discloses a structured list instead (see `subprocessors` below).
 *
 * rii.link trades as an Einzelunternehmen (German sole trader), so §5 DDG
 * requires the operator's own name and address rather than a company name, and
 * there is no commercial register entry, no managing director and no VAT ID to
 * disclose. The imprint deliberately does not render those rows.
 *
 * Values still wrapped in square brackets are unfilled. `isPending()` detects
 * them so `<LegalValue>` can highlight the gap; once a real value replaces the
 * bracketed string the highlight disappears on its own — nothing else to edit.
 * That is why every value here is a string, including the numeric ones: a
 * pending fact is a bracketed placeholder, which a number cannot be.
 *
 * Revision dates are **not** here. They are per document *and* per locale — a
 * German text revised on a different day than the English one would otherwise
 * show a false date — so each content module carries its own.
 *
 * On incorporation (a UG or GmbH), this file is most of the migration but not
 * all of it: `operator` becomes the company rather than a natural person,
 * `vatId` gets a real USt-IdNr., §5 DDG additionally requires the register
 * court, HRB number and managing director(s) — three rows the imprint does not
 * render today — and `jurisdictionCity` becomes usable, since §38 ZPO only
 * permits an agreed venue between merchants.
 */

/**
 * ISO 3166-1 alpha-2, plus one sentinel. Country *names* are prose and differ
 * per language, so they live in `content/{locale}/strings.ts` as a
 * `Record<CountryCode, string>` — which means adding a country here is a type
 * error in every locale that has not followed. That is the property a plain
 * string could not give.
 *
 * `PENDING` is the honest answer for a processor whose deployed region we have
 * not confirmed. It maps to a visibly unfinished phrase per locale rather than
 * silently defaulting to a country we would then be asserting.
 */
export type CountryCode = 'DE' | 'NL' | 'US' | 'PENDING';

/**
 * A third party that receives personal data. Purposes are **keys**, not
 * sentences: an English purpose string rendered mid-sentence in the operative
 * German document is exactly the bug this indirection prevents. Each locale
 * maps the key to its own noun phrase in `content/{locale}/strings.ts`, so a
 * new processor is a type error in every locale that has not followed.
 */
export type Subprocessor = {
  name: string;
  purposeKey:
    | 'app-hosting'
    | 'site-hosting'
    | 'mail-infrastructure'
    | 'tls-termination'
    | 'transactional-email'
    | 'object-storage'
    | 'bot-mitigation'
    | 'ai-assistant'
    | 'url-safety'
    | 'operator-alerts';
  /** Where the processing happens — not where the vendor is incorporated. */
  countryCode: CountryCode;
  /**
   * Art. 44–49 basis, only where processing leaves the EEA. `null` means no
   * transfer to disclose, which is the answer we want for most of these.
   */
  transferBasis: 'scc' | 'adequacy' | null;
};

export const legal = {
  /** §5 DDG — the operator. For a sole trader this is a natural person. */
  operator: {
    name: 'Florian Preusner',
    street: 'Freester Weg 10c',
    postalCity: '13503 Berlin',
    countryCode: 'DE' as const,
  },

  contact: {
    /**
     * All four are live mailboxes (confirmed by the operator, 2026-08-12), as is
     * `security@` via `public/.well-known/security.txt`. They must stay that
     * way: §5 DDG requires the imprint's contact channel to actually work, and a
     * bouncing address is the specific defect that draws an Abmahnung.
     */
    general: 'support@rii.link',
    sales: 'sales@rii.link',
    privacy: 'privacy@rii.link',
    security: 'security@rii.link',
    /**
     * Art. 11/12 DSA — single point of contact for authorities and recipients.
     *
     * Why it exists: DSA Art. 3(g) defines hosting as storage of information
     * provided by, and at the request of, a recipient. rii.link stores redirect
     * targets and plain-response bodies at its customers' request and serves
     * them from customer domains, which is at least arguably hosting. The
     * micro-enterprise exemption in Art. 19 covers the Section 3 duties
     * (Art. 20–28), not the Section 1 points-of-contact duties. Publishing one
     * is cheaper than leaving it unresolved.
     */
    dsa: 'legal@rii.link',
    /**
     * §5 DDG requires a second channel allowing rapid contact alongside email.
     * Per ECJ C-298/07 a phone number is not mandatory where another channel
     * answers promptly, so the imprint points at /contact instead — the address
     * above is residential and a published number is hard to walk back. Set a
     * number here and the imprint switches to showing it.
     */
    phone: null as string | null,
  },

  /**
   * No USt-IdNr. issued yet, so none is displayed — §5 DDG only requires one
   * where it exists, and a Steuernummer is neither required nor advisable to
   * publish. Set this and the imprint's VAT section appears on its own.
   *
   * Note for #366: selling directly to consumers means PAngV price display
   * rules apply, and §19 UStG (Kleinunternehmerregelung) requires the
   * corresponding note. The pricing page currently quotes USD, which is a
   * problem for German consumer sales independent of this field.
   */
  vatId: null as string | null,

  /**
   * §2 of the terms. 16 is the age from which Art. 8(1) GDPR lets a person
   * consent to information-society services without their guardian, and Germany
   * did not lower it — so this tracks the German floor, not the 13 a US-drafted
   * policy would state.
   */
  minimumAge: '16',

  /** GDPR supervisory authority — the one for the operator's federal state. */
  supervisoryAuthority:
    'Berliner Beauftragte für Datenschutz und Informationsfreiheit',

  /**
   * Named rather than categorised. Art. 13(1)(e) permits "recipients **or**
   * categories of recipients", and Paperstand uses categories so a provider
   * swap costs no policy edit — a fair trade for an app with three processors.
   * rii.link is a routing platform: its customers' *visitors* are the data
   * subjects, those customers run security reviews, and /security already
   * publishes a subprocessor table. Naming them is the more defensible side of
   * that trade here, and it keeps this file and that page from drifting apart.
   *
   * Deliberately absent, because neither receives data:
   * - **MaxMind GeoLite2** — a local `.mmdb` file read in-process
   *   (`backend/src/api/services/geoip.py`, `routing/src/routing/geoip.py`).
   *   No lookup leaves the machine.
   * - **GlitchTip** — self-hosted on our own infrastructure, not a SaaS.
   *   Naming a processor that receives nothing is inaccurate in a document
   *   people are entitled to rely on.
   */
  subprocessors: [
    {
      name: 'Railway',
      purposeKey: 'app-hosting',
      /**
       * Amsterdam (confirmed by the operator, 2026-08-13). Railway defaults new
       * services to US West and the region is chosen per service, so this needs
       * re-checking whenever a service is added — it is not a property of the
       * account.
       *
       * Note the mismatch this exposes: /security and /solutions/enterprise say
       * "every byte stored and served from Frankfurt". The application tier is
       * in Amsterdam. Both are EU, so the residency claim holds in substance,
       * but the word "Frankfurt" is wrong for this component (#392, #398).
       */
      countryCode: 'NL',
      /**
       * Processing is in the EEA, so there is no transfer to legitimise in the
       * Art. 44 sense. The safeguard is recorded anyway because Railway is
       * US-established and support access from outside the EEA is possible;
       * §8 of the privacy policy is worded to cover exactly that case.
       */
      transferBasis: 'scc',
    },
    {
      name: 'Vercel',
      purposeKey: 'site-hosting',
      countryCode: 'US',
      transferBasis: 'scc',
    },
    {
      /*
       * Same vendor, second row rather than a merged purpose string: Vercel
       * BotID runs site-wide (every page, unconditionally) and is a
       * genuinely separate processing purpose from serving the site itself.
       * The `Subprocessor` type is a name+purpose pair, so one vendor with
       * two purposes is two rows, not one row stretched to cover both.
       */
      name: 'Vercel',
      purposeKey: 'bot-mitigation',
      countryCode: 'US',
      transferBasis: 'scc',
    },
    {
      name: 'Hetzner Online GmbH',
      purposeKey: 'mail-infrastructure',
      countryCode: 'DE',
      transferBasis: null,
    },
    {
      /*
       * Our own TLS edge (connect.rii.link: Hetzner load balancer + Caddy in
       * Falkenstein, `infrastructure/modules/edge`), in front of every custom
       * and short-link domain since 2026-10-06. It replaced Approximated (US),
       * cancelled 2026-10-11. Second Hetzner row for the second purpose, as
       * with Vercel above.
       */
      name: 'Hetzner Online GmbH',
      purposeKey: 'tls-termination',
      countryCode: 'DE',
      transferBasis: null,
    },
    {
      name: 'Twilio SendGrid',
      purposeKey: 'transactional-email',
      countryCode: 'US',
      transferBasis: 'scc',
    },
    {
      name: 'Amazon Web Services',
      purposeKey: 'object-storage',
      /**
       * Frankfurt / eu-central-1 (confirmed by the operator, 2026-08-13), which
       * matches what /security claims.
       *
       * #392 is therefore narrower than it first looked: production is correct,
       * but `backend/src/api/config.py` still *defaults* `S3_REGION=us-east-1`,
       * so any environment that forgets the override silently lands outside the
       * EU. The fix is the default and a Terraform pin, not a migration.
       */
      countryCode: 'DE',
      /** As with Railway: EEA processing, safeguard recorded for support access. */
      transferBasis: 'scc',
    },
    {
      name: 'Cloudflare',
      purposeKey: 'bot-mitigation',
      /*
       * Turnstile is loaded ONLY when a visitor starts filling in a form that
       * sends email or alerts (contact, integration request, abuse report,
       * changelog/status subscribe, emailed admin link — components/forms/
       * useHumanCheck.tsx, website#302), or when a link-shortening submission
       * has been flagged as automated (TurnstileChallenge.tsx). A visitor who
       * only reads pages never contacts Cloudflare, which is what lets §6
       * keep its no-cookies statement for normal use.
       */
      countryCode: 'US',
      /*
       * SCCs rather than an EU-US DPF adequacy claim, matching the Vercel
       * entries. Asserting adequacy would mean tracking one more
       * certification's status in a document people are entitled to rely on.
       */
      transferBasis: 'scc',
    },
    {
      /*
       * The dashboard assistant (backend#450, `backend/src/api/agent/`). Each
       * turn sends the conversation, a short session header (workspace name,
       * plan, the user's role, timezone and the page they have open —
       * `agent/prompts.py`) and the results of the tools the model calls, which
       * is workspace content: domains, paths, analytics, team members, email
       * forwarding logs. Conversation titles are generated by a second, smaller
       * model call on the same API. Nothing is sent until someone uses the
       * assistant (website#276).
       */
      name: 'Anthropic',
      purposeKey: 'ai-assistant',
      countryCode: 'US',
      transferBasis: 'scc',
    },
    {
      /*
       * Google Web Risk `uris.search` (backend#484, `services/url_safety.py`).
       * The request carries the destination URL and the threat types only, and
       * it is made from our server, so Google never sees who created the link.
       * Every write path that sets a destination calls it; the worker also
       * re-checks destinations created or edited in the last 7 days and our own
       * shared-domain short URLs (`services/url_safety_monitor.py`).
       *
       * A URL can still be personal data (a path or query string may name
       * someone), which is why this is listed at all.
       */
      name: 'Google Cloud (Web Risk)',
      purposeKey: 'url-safety',
      countryCode: 'US',
      transferBasis: 'scc',
    },
    {
      /*
       * Operator alerts (`backend/src/api/services/abuse_alerts.py`): each
       * abuse report triggers an alert in our internal Slack channel with the
       * defanged reported URL, category and a link to the admin report page —
       * never the description or the reporter's email. Web Risk hits go the
       * same way. Region: US is Slack's default for workspaces
       * without a data-residency add-on — confirm against the workspace
       * settings.
       */
      name: 'Slack',
      purposeKey: 'operator-alerts',
      countryCode: 'US',
      transferBasis: 'scc',
    },
  ] satisfies readonly Subprocessor[],

  retention: {
    /**
     * How long click/analytics records (`request_log`) are kept — a key, mapped
     * to prose per locale.
     *
     * This was a bracketed placeholder, which was the wrong shape: a retention
     * period is a sentence, and a sentence cannot live here once there are two
     * languages. The placeholder would have rendered highlighted **and in
     * English** inside the German document, which is precisely the bug the
     * keys-not-prose rule exists to prevent.
     *
     * `account-lifetime` is the truth today: there is no retention enforcement
     * and no purge job (#376), so records live as long as the account does. The
     * pricing page advertises 30 days / 1 year / unlimited, which is not
     * implemented — stating that here would be a documented commitment already
     * in breach. Switch to `plan-based` when #376 ships.
     */
    analyticsBasis: 'account-lifetime' as 'account-lifetime' | 'plan-based',
    /**
     * Visitor IP handling — a key, mapped to prose per locale.
     *
     * #407 truncates visitor IPs at the durable-storage boundary: IPv4 to /24
     * and IPv6 to /48. GeoIP and routing decisions happen first, then only the
     * coarse network prefix reaches PostgreSQL.
     */
    ipHandling: 'anonymised-at-ingestion' as
      | 'anonymised-at-ingestion'
      | 'stored-in-full',
    /** Post-deletion window for tenant data, matching /security's claim. */
    accountDeletionDays: '30',
    /**
     * Dashboard-assistant conversations (messages and pending actions) are
     * deleted this many days after their last activity by the worker's
     * `agent_retention` job — `AGENT_RETENTION_DAYS`, default 180, not
     * overridden in production Terraform. The monthly usage counters in
     * `agent_usage_monthly` hold no conversation content and are kept.
     */
    assistantConversationDays: '180',
    /**
     * Abuse reports: the reporter's email, the description and the truncated IP
     * prefix are cleared this many months after the report is resolved
     * (actioned, dismissed or auto-dismissed) by the worker's
     * `abuse_report_retention` job — `ABUSE_REPORT_RETENTION_DAYS`, default 365
     * (backend#495). The anonymous record stays. Slack alerts never carry
     * reporter data, so there is nothing to scrub there.
     */
    abuseReportPersonalDataMonths: '12',
  },

  /**
   * §38 ZPO — a place of jurisdiction may only be agreed between merchants. A
   * sole trader who is not a registered Kaufmann cannot rely on this, so the
   * terms fall back to the statutory rules while this is null.
   */
  jurisdictionCity: null as string | null,
} as const;

/** True while a value is still a bracketed placeholder rather than a real fact. */
export function isPending(value: string): boolean {
  return value.trim().startsWith('[');
}

/**
 * True while any processor's place of processing is unconfirmed.
 *
 * Defined here rather than in each locale so there is one definition, and
 * annotated `boolean` deliberately: `as const` narrows `countryCode` to the
 * literals actually present, so without the annotation TypeScript would fold
 * this to `false` and then reject the comparison as unreachable. The cast says
 * the same thing — we are asking a question of the general type, because the
 * answer is meant to change when a processor is added.
 *
 * Sections that qualify their claims for unconfirmed regions render on this,
 * so the qualification appears and disappears with the fact.
 */
export const hasUnconfirmedRegion: boolean = legal.subprocessors.some(
  (p) => (p.countryCode as CountryCode) === 'PENDING'
);
